import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import imageDetail from '../../../assets/images/ImgDetails/Banner_placeholder.png';
import styles from './EditProfessionalInfo.module.scss';
import { ButtonUi } from '../../ui/ButtonUi/ButtonUi';

import { useEditProfessionalInfo } from '../../../hooks/useEditProfessionalInfo';
import { ProfessionalInfoProps } from '../../../types';
import { ProfessionalDataForm } from '../components/DataForm/ProfessionalDataForm/ProfessionalDataForm';
import { ProfessionalMediaSection } from '../components/MediaSection/ProfessionalMediaSection';
import { ProfessionalFormActions } from '../components/FormActions/ProfessionalFormActions';
import ProfessionalEditTabs from '../components/EditTabs/ProfessionalEditTabs';
import { useUnsavedChanges } from '../../../context/UnsavedChangesContext';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';

export function ProfessionalInfoSection(props: ProfessionalInfoProps) {
    const { style, allowInternalToggle = true, editable = false, professions, loading = false } = props;
    const { t } = useTranslation();
    const { isReminderActive } = useUnsavedChanges();

    const {
        form,
        errors,
        submitting,
        isEditMode,
        setInternalEditing,
        setNewImageFile,
        setFieldValue,
        updateList,
        handleOtherProfessionsChange,
        handleSubmit,
        handleCancel,
        newImageFile,
        handlePhotoCropSave,
        handlePreview,
        settings,
        isDirty,
        lastSaved
    } = useEditProfessionalInfo(props);

    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const handlePreviewClick = () => {
        if (isDirty) {
            setShowPreviewModal(true);
        } else {
            handlePreview();
        }
    };


    const imageUrl = props.user.profile.details?.banner_image
        ? `${process.env.REACT_APP_API_URL}${props.user.profile.details.banner_image}`
        : imageDetail;

    return (
        <div className={styles['professional-detail__main']} style={style}>
            {allowInternalToggle && !editable && (
                <div className={styles['profile-actions-sticky']}>
                    <div className={styles['profile-actions']} aria-label="Acciones de edición del perfil">
                        <button 
                            className={styles['btn-edit']}
                            id="btn-edit-profile"
                            disabled={isEditMode}
                            aria-disabled={isEditMode ? "true" : "false"}
                            type="button"
                            onClick={() => !isEditMode && setInternalEditing(true)}
                        >
                            {isEditMode ? t('appAdmin.EditProfessionalInfo.editing', 'Editando') : t('appAdmin.EditProfessionalInfo.editProfile', 'Editar perfil público')}
                        </button>
                        <button 
                            className={styles['btn-preview']}
                            id="btn-preview"
                            type="button"
                            onClick={handlePreviewClick}
                        >
                            {t('appAdmin.EditProfessionalInfo.preview', 'Previsualizar')}
                        </button>
                    </div>
                </div>
            )}
            <div className={styles['media-section']}>
                <ProfessionalMediaSection
                    isEditMode={isEditMode}
                    form={form}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setNewImageFile={setNewImageFile}
                    newImageFile={newImageFile}
                    handlePhotoCropSave={handlePhotoCropSave}
                    imageUrl={imageUrl}
                    user={props.user}
                />
            </div>

            <div className={styles['content-section']}>
                <ProfessionalDataForm
                    isEditMode={isEditMode}
                    user={props.user}
                    form={form}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    professions={professions}
                    handleOtherProfessionsChange={handleOtherProfessionsChange}
                    aboutSettings={settings?.about}
                />

                <ProfessionalEditTabs
                    editMode={isEditMode}
                    professionalInfo={form.details.professional_info}
                    details={form.details}
                    onDetailsChange={(key: string, value: unknown) => setFieldValue(`details.${key}`, value)}
                    studies={form.studies ? [...form.studies] : []}
                    onStudiesChange={(updater) => updateList('studies', updater)}
                    errors={errors}
                    professionalInfoSettings={settings?.professional_info}
                />

                <ProfessionalFormActions
                    isEditMode={isEditMode}
                    submitting={submitting}
                    loading={loading}
                    onExit={handleCancel}
                    onSaveAndExit={handleSubmit}
                    onSave={() => handleSubmit(undefined, true)}
                    isDirty={isDirty}
                    lastSaved={lastSaved}
                    isReminder={isReminderActive}
                />

                <UnsavedChangesModal
                    open={showPreviewModal}
                    onClose={() => setShowPreviewModal(false)}
                    onSaveAndExit={async () => {
                        setShowPreviewModal(false);
                        await handleSubmit(undefined, true);
                        handlePreview();
                    }}
                    isPreviewMode={true}
                />
            </div>
        </div>
    );
}

export default ProfessionalInfoSection;
